import Image from 'next/image';
import Link from 'next/link';
import { BiStore } from 'react-icons/bi';
import { FaShapes } from 'react-icons/fa';
import { HiCollection, HiLocationMarker } from 'react-icons/hi';
import { useBoolean } from 'usehooks-ts';

import { getSubCategoryById } from '@/lib/helpers';
import { IUserData } from '@/modules/core/types';
import Text from '@/tayara-kit/Text';

import { TkCard } from './icons';

type ShopCardType = {
  userDetails: IUserData;
  countAdByUser?: number;
  showCountAds?: boolean;
  NeufPage?: boolean;
};
const ShopCard = ({ userDetails, NeufPage }: ShopCardType) => {
  const { value: brokenImage, setValue: setBrokenImage } = useBoolean(false);
  const userAvatar = userDetails.avatar || '';
  return (
    <div className="relative w-[13rem] h-[28rem]">
      <Link
        passHref
        href={`${
          NeufPage
            ? `/immoNeuf/${userDetails.kcid}`
            : `/shop/${userDetails.kcid}`
        }`}
        className="cursor-pointer h-full"
      >
        <div
          className={`relative h-full  w-full  rounded-3xl flex md:justify-center flex-col  p-6 border-[1px] border-gray-300 hover:border-blue-300/60  md:aspect-grid bg-gradient-to-b from-neutral-500/10 to-neutral-500/0`}
        >
          <div className=" absolute top-[2%] right-[30%] md:right-[25%] -z-20 flex gap-4 text-blue-100/50 ">
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[10%]"
              size={'100%'}
            />
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[-5%]"
              size={'100%'}
            />
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[10%]"
              size={'100%'}
            />
          </div>
          <div className=" absolute bottom-[53%] -z-20 right-[30%] md:right-[16%] flex gap-3 text-blue-300/40 ">
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[-10%]"
              size={'100%'}
            />
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[10%]"
              size={'100%'}
            />
            <TkCard
              className="w-9 h-16 border-2 border-white  mt-[0%]"
              size={'100%'}
            />
          </div>
          <div className=" absolute bottom-[5%] -z-20 right-[30%] md:right-[6%] flex gap-1 text-blue-300/40 ">
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[20%]"
              size={'100%'}
            />
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[-10%]"
              size={'100%'}
            />
            <TkCard
              className="w-9 h-16 border-2 border-white mt-[0%]"
              size={'100%'}
            />
            <TkCard
              className="w-9 h-16 border-2 border-white  mt-[20%]"
              size={'100%'}
            />
          </div>
          <div className="flex flex-col justify-between">
            <div className="flex items-center justify-center mx-auto transition-transform active:scale-90 avatar relative ">
              <div className="mask mask-squircle absolute w-[110px] blur-2xl shadow-md shadow-white bg-white"></div>
              <div className={`mask mask-squircle w-[100px] `}>
                {userDetails.avatar && !brokenImage ? (
                  <Image
                    loading="lazy"
                    width={100}
                    height={100}
                    className="w-full"
                    src={
                      userAvatar.includes('http')
                        ? `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar.substring(
                            userAvatar.lastIndexOf('/') - 2
                          )}&w=100`
                        : `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar}&w=100`
                    }
                    unoptimized
                    alt="tayara publisher profile picture"
                    onError={() => setBrokenImage(true)}
                    onLoadingComplete={(result) => {
                      if (result.naturalWidth === 0) {
                        // If the naturalWidth of the image is 0 -> Broken image
                        setBrokenImage(true);
                      }
                    }}
                  />
                ) : (
                  <div className="bg-white text-blue-400">
                    <BiStore className="p-4" size={'100%'} />
                  </div>
                )}
              </div>
            </div>

            <Text
              text={NeufPage ? 'Projet' : 'Boutique'}
              className="text-2xs font-semibold leading-3 text-blue-600 mt-16"
            />
            {/* Publisher name */}
            <Text
              text={userDetails.fullname?.toLowerCase() || ' sur tayara'}
              className={`text-sm font-semibold capitalize text-blue-900/70 my-2`}
            />
          </div>

          <div
            className={`flex flex-col ${
              userDetails.address ? 'justify-between' : 'justify-between'
            } h-28 border-t p-4 `}
          >
            <div className="flex flex-col gap-y-2 mt-2">
              {userDetails.address && (
                <div
                  className={`relative flex text-2xs items-center space-x-1 text-blue-800/60`}
                >
                  <div className="absolute top-0 -left-4 p-1">
                    <FaShapes size={12} />
                  </div>
                  <Text
                    className="mt-[2px]"
                    text={
                      getSubCategoryById(userDetails.categoryId) &&
                      getSubCategoryById(userDetails.categoryId).name
                    }
                  />
                </div>
              )}
              {/* Shop location */}
              {userDetails.address && (
                <div
                  className={`relative flex text-2xs items-center space-x-1 text-blue-800/60 `}
                >
                  <div className="absolute top-0 -left-4 p-1">
                    <HiLocationMarker size={12} />
                  </div>
                  <Text
                    className="mt-[1.5px] truncate"
                    text={userDetails.address || ''}
                  />
                </div>
              )}
              <div
                className={`relative flex text-2xs items-center space-x-1 text-blue-800/60`}
              >
                <div className="absolute top-0 -left-4 p-1">
                  <HiCollection size={12} />
                </div>
                <Text text={`Voir les annonces`} className="mt-[2px]" />
              </div>
            </div>
            {/* Strore icon detail */}
            {/* <div
              className={` rounded-md p-1.5 w-[30px] border-[1px] border-blue-400 bg-white text-blue-400 mt-4 mx-auto`}
            >
              <BiStore size={16} className="w-full h-full" />
            </div> */}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ShopCard;
